import { Condition, FieldMatchNode, MatchExpression, MatchExpressionMultiMatch, QueryParameters, isFieldMatchNode } from './types.js'
import { Index, IndexMap } from '../indexing/document-index'
import { SingleQueryStats } from 'model/indexing/query-stats.js'
import {
  DocumentMatchIterator,
  DocumentMatchIteratorAllDocuments,
  DocumentMatchIteratorJoined,
  DocumentMatchIteratorOverNoDocuments,
  DocumentMatchIteratorUsingId,
  DocumentMatchIteratorUsingIndex,
  IterateAllDocuments,
  IterateNoDocuments,
  IterateOverId,
  IterateOverIndex,
  IterateOverSubIterators,
  KMergedIntersection,
  KMergedUnion,
  describeIterator,
  isIteratorOverAllDocuments,
  isIteratorOverNoDocuments,
  isIteratorUsingId,
  isIteratorUsingIndexOfDocuments,
  isIteratorUsingJoinsOfIterators,
} from './iterator-types.js'

function optimizeSiblingIterators(
  iterators: DocumentMatchIterator[],
  options?: OptimizationOptions,
  stats?: SingleQueryStats,
  operatorType: 'intersect' | 'union' = 'intersect'
): DocumentMatchIterator[] {
  if (!options?.enableCostBasedShortcut || iterators.length <= 1) {
    return iterators
  }

  // Never optimize union queries - they need all iterators for correctness
  if (operatorType === 'union') {
    return iterators
  }

  // Sort iterators by cost (smallest first)
  const sortedIterators = [...iterators].sort((a, b) => a.cost - b.cost)
  const selectivityThreshold = options.selectivityThreshold ?? 100
  const costThresholdMultiplier = options.costThresholdMultiplier ?? 10

  // Apply optimization rules as described:
  // 1. If any iterator is empty, replace all with empty
  if (sortedIterators.some(it => it.type === IterateNoDocuments || it.cost === 0)) {
    stats?.mark('sibling-optimization:empty-iterator-found')
    return [{ type: IterateNoDocuments, cost: 0, isSortable: true }]
  }

  // 2. For intersection: Only apply aggressive optimization if we can guarantee correctness
  // Check first iterator against selectivity threshold - but keep more conservative
  const firstIterator = sortedIterators[0]

  // If the first iterator is already very selective, we can optimize
  if (firstIterator.cost < selectivityThreshold) {
    stats?.mark('sibling-optimization:selective-first-iterator', {
      firstCost: firstIterator.cost,
      selectivityThreshold,
      totalIterators: sortedIterators.length
    })

    // Record in the format expected by tests
    stats?.mark('graph-optimization:intersection-iterators-removed', {
      originalCount: sortedIterators.length,
      optimizedCount: 1,
      removedCount: sortedIterators.length - 1,
      mostSelectiveCost: firstIterator.cost,
      selectivityThreshold,
      costThresholdMultiplier,
      mergeCostEstimate: sortedIterators.reduce((sum, it) => sum + it.cost, 0),
      triggers: {
        isHighlySelective: true,
        isBelowCostThreshold: false,
        primaryTrigger: 'highly-selective'
      }
    })

    return [firstIterator]
  }

  // Less aggressive pairwise comparison
  let optimizedIterators = [...sortedIterators]

  for (let i = 0; i < sortedIterators.length - 1; i++) {
    const currentIterator = sortedIterators[i]
    const nextIterator = sortedIterators[i + 1]

    // Much more conservative threshold - only remove if the cost difference is extreme
    const costRatio = nextIterator.cost / currentIterator.cost // E.g  1000 / 100 => 10.0
    const nextIteratorIsMuchMoreExpensive = costRatio > costThresholdMultiplier // E.g 10.0 > 10 => true

    if (nextIteratorIsMuchMoreExpensive) {
      stats?.mark('sibling-optimization:cost-threshold-hit', {
        currentCost: currentIterator.cost,
        nextCost: nextIterator.cost,
        costThresholdMultiplier,
        ratio: costRatio,
        removedCount: sortedIterators.length - (i + 1)
      })

      // Record in the format expected by tests
      stats?.mark('graph-optimization:intersection-iterators-removed', {
        originalCount: sortedIterators.length,
        optimizedCount: i + 1,
        removedCount: sortedIterators.length - (i + 1),
        mostSelectiveCost: sortedIterators[0].cost,
        selectivityThreshold,
        costThresholdMultiplier,
        mergeCostEstimate: sortedIterators.reduce((sum, it) => sum + it.cost, 0),
        triggers: {
          isHighlySelective: false,
          isBelowCostThreshold: true,
          primaryTrigger: 'below-cost-threshold'
        }
      })

      // Remove all iterators from position i+1 onwards
      optimizedIterators = sortedIterators.slice(0, i + 1)
      break
    }
  }

  return optimizedIterators
}

export interface OptimizationOptions {
  /**
   * Enable cost-based optimization for query intersection processing.
   *
   * ## What it does:
   * When enabled, the query engine can remove high-cost iterators from intersection (AND)
   * queries when a highly selective iterator is present. This reduces the computational
   * cost of k-way merge operations by eliminating expensive document enumeration.
   *
   * ## Why it's safe:
   * Iterator removal is semantically safe because:
   * 1. **Post-filtering ensures correctness**: The removed conditions are still enforced
   *    by match.ts during the final document filtering phase
   * 2. **Superset enumeration**: The remaining iterator(s) return a superset of documents
   *    that potentially match the query, then post-filtering removes false positives
   * 3. **Performance vs correctness trade-off**: We exchange some enumeration overhead
   *    for significantly reduced k-merge complexity
   *
   * ## Optimization applies to:
   * - ✅ Intersection queries (AND): Safe to remove expensive iterators
   * - ❌ Union queries (OR): Never optimized - would change query semantics
   *
   * @default false
   */
  enableCostBasedShortcut?: boolean

  /**
   * Fixed threshold for identifying highly selective iterators.
   *
   * ## What it does:
   * If an iterator's cost (estimated number of documents) is below this threshold,
   * it's considered "highly selective" and triggers optimization. Other iterators
   * in the intersection may be removed to reduce k-merge overhead.
   *
   * ## When to use:
   * - Set higher (e.g., 5000) for aggressive optimization in large datasets
   * - Set lower (e.g., 100) for conservative optimization
   * - Use when you have predictable selective queries (e.g., ID lookups, status filters)
   *
   * ## Safety:
   * This trigger is conservative - only activates when there's a genuinely selective
   * iterator that can efficiently enumerate a small candidate set. Post-filtering
   * via match.ts ensures all original conditions are still enforced.
   *
   * @default 100
   */
  selectivityThreshold?: number

  /**
   * Cost ratio threshold for pairwise iterator optimization decisions.
   *
   * ## What it does:
   * When comparing consecutive iterators (sorted by cost), removes an iterator
   * and all subsequent ones if the cost ratio exceeds this threshold.
   *
   * ## How it works:
   * - Higher multiplier (e.g., 100) = more aggressive optimization
   * - Lower multiplier (e.g., 2) = very conservative optimization
   * - Formula: `nextIteratorCost / currentIteratorCost > costThresholdMultiplier`
   *
   * ## Use cases:
   * - High multiplier (100): Keep iterators even if they're much more expensive
   * - Low multiplier (2): Remove iterators that are just 2x more expensive
   * - Medium multiplier (10): Balanced approach, removes very expensive iterators
   *
   * ## Example:
   * Iterator costs [100, 500, 5000] with multiplier 10:
   * - First comparison: 500/100 = 5 ≤ 10 → keep both iterators
   * - Second comparison: 5000/500 = 10 ≤ 10 → keep all iterators
   * - If multiplier was 5: 5000/500 = 10 > 5 → remove the 5000-cost iterator
   *
   * ## Safety:
   * This optimization is safe for intersection queries because post-filtering
   * ensures correctness. Higher multipliers are safer but may reduce optimization.
   *
   * @default 10 (remove iterators 10x more expensive than the previous one)
   */
  costThresholdMultiplier?: number

  /**
   * When false, keeps individual field match parameters as-is and prevents the
   * query graph optimizer from rewriting them into a single array parameter.
   *
   * @default false
   */
  mergeFieldParameters?: boolean
}

// Legacy type alias for backward compatibility
export type EnumerationOptions = OptimizationOptions

export function buildIteratorForGraph(
  node: MatchExpression | Condition,
  parameters: QueryParameters,
  indexes: IndexMap,
  stats?: SingleQueryStats,
  options?: OptimizationOptions,
  isTopLevelCall = true
): DocumentMatchIterator {
  let iterator: DocumentMatchIterator

  if (isFieldMatchNode(node)) {
    iterator = buildIteratorForFieldMatch(node, parameters, indexes, stats)
  } else if ('conditions' in node && node.conditions.length === 0) {
    stats?.mark('empty-conditions')
    iterator = { cost: 0, type: IterateNoDocuments, isSortable: true }
  } else if ('conditions' in node && node.conditions.length === 1) {
    iterator = buildIteratorForGraph(node.conditions[0], parameters, indexes, stats, options, false)
  } else if ('operator' in node) {
    iterator = buildIteratorForJoinedCondition(node as MatchExpressionMultiMatch, parameters, indexes, stats, options)
  } else {
    throw new Error(`Not supported ${JSON.stringify(node)}`)
  }

  if (isTopLevelCall) {
    stats?.phase('final-query-structure', { plan: describeIterator(iterator) })
  }

  return iterator
}

function buildIteratorForFieldMatch(
  condition: FieldMatchNode,
  parameters: QueryParameters,
  indexes: IndexMap,
  stats?: SingleQueryStats
): DocumentMatchIterator {
  // Handle NOT queries (termPrefix: "-")
  if (condition.termPrefix === '-') {
    return buildIteratorForNotFieldMatch(condition, parameters, indexes, stats)
  }

  if (condition.field === 'id' && 'term' in condition) {
    const key = parameters[condition.term.id]

    return {
      type: IterateOverId,
      cost: 1,
      id: key,
      isSortable: true
    }
  }

  if (condition.field === 'id') {
    // The document map is keyed by id, but id is not a normal field index.
    // Range matching is still supported by QueryMatcher, so enumerate all
    // candidates here and let the matcher apply the range predicate.
    stats?.mark('range-query:id-field-all-documents')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  const matchingIndex = indexes[condition.field]

  if (matchingIndex) {
    if ('term_min' in condition || 'term_max' in condition) {
      let keyMin = parameters[condition.term_min?.id]
      let keyMax = parameters[condition.term_max?.id]

      if (Array.isArray(keyMin) || Array.isArray(keyMax)) {
        throw new Error('Not implemented')
      }

      stats?.mark('range-iterator', { keyMin, keyMax })

      return buildIteratorForFieldRange(keyMin, keyMax, matchingIndex, stats)
    } else if ('term' in condition) {
      const key = parameters[condition.term.id]

      if (Array.isArray(key)) {
        let cost = 0
        const subIterators: DocumentMatchIterator[] = []

        stats?.mark('multi-match')

        for (const value of key) {
          const subIterator = buildIteratorForFieldValue(value, matchingIndex, stats)
          cost += subIterator.cost
          subIterators.push(subIterator)
        }

        // For multi-value fields, this is a union operation - do not apply intersection optimizations
        const optimizedSubIterators = subIterators // Keep all iterators for union
        const optimizedCost = cost

        // Use K-merge union if all iterators are sortable
        if (optimizedSubIterators.every(it => it.isSortable)) {
          return {
            type: KMergedUnion,
            cost: optimizedCost,
            iterators: optimizedSubIterators,
            isSortable: true
          }
        } else {
          return {
            type: IterateOverSubIterators,
            operator: 'union',
            cost: optimizedCost,
            iterators: optimizedSubIterators as (
              DocumentMatchIteratorUsingId | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorJoined
            )[],
            isSortable: false
          }
        }
      } else {
        return buildIteratorForFieldValue(key, matchingIndex, stats)
      }
    }

    throw new Error('Not supported')
  } else {
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }
}

export function buildIteratorForFieldRange(fieldMin: any, fieldMax: any, index: Index, stats?: SingleQueryStats): DocumentMatchIterator {
  if (fieldMin === '*' && fieldMax === '*') {
    stats?.mark('all-documents-iterator')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  if (typeof fieldMin === 'string') {
    if (fieldMin === '*') {
      fieldMin = '\x00' // Represents the smallest possible string
    } else if (fieldMin.endsWith('*')) {
      fieldMin = fieldMin.slice(0, -1)
    }
  }

  if (typeof fieldMax === 'string') {
    if (fieldMax === '*') {
      fieldMax = '\uffff' // Represents a very large string (for comparison)
    } else if (fieldMax.endsWith('*')) {
      fieldMax = fieldMax.slice(0, -1)
    }
  }

  const entriesWithKeys = index.getEntriesForRange(fieldMin, fieldMax)

  if (entriesWithKeys && entriesWithKeys.length > 0) {
    const subIterators: DocumentMatchIteratorUsingIndex[] = []

    for (const [key, idIterator] of entriesWithKeys) {
      if (idIterator.size > 0) {
        const numberOfDocuments = idIterator.size
        subIterators.push({
          type: IterateOverIndex,
          index,
          key: key as string,
          cost: numberOfDocuments,
          idIterator,
          isSortable: index.isBTree
        })
      }
    }

    const subIteratorsMatched = subIterators.length

    if (subIterators.length === 0) {
      stats?.mark('range-iterator:none', { fieldMin, fieldMax, cost: 0 })
      return { type: IterateNoDocuments, index, key: fieldMin ?? fieldMax, cost: 0, isSortable: true }
    }

    if (subIterators.length === 1) {
      stats?.mark('range-iterator:single', { fieldMin, fieldMax, cost: subIterators[0].cost })
      return subIterators[0]
    }

    const totalCost = subIterators.reduce((acc, iterator) => acc + iterator.cost, 0)
    stats?.mark('range-iterator:union', { fieldMin, fieldMax, subIteratorsMatched, cost: totalCost })

    // For range queries, this is a union operation - do not apply intersection optimizations
    const optimizedSubIterators = subIterators // Keep all iterators for union
    const optimizedCost = totalCost

    // Use K-merge union if all iterators are sortable
    if (optimizedSubIterators.every(it => it.isSortable)) {
      return {
        type: KMergedUnion,
        cost: optimizedCost,
        iterators: optimizedSubIterators,
        isSortable: true
      }
    } else {
      return {
        type: IterateOverSubIterators,
        operator: 'union',
        cost: optimizedCost,
        iterators: optimizedSubIterators as (
          DocumentMatchIteratorUsingId | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorJoined
        )[],
        isSortable: false
      }
    }
  } else {
    return { type: IterateNoDocuments, index, key: fieldMin ?? fieldMax, cost: 0, isSortable: true }
  }
}

export function buildIteratorForFieldValue(fieldValue: any, index: Index, stats?: SingleQueryStats): DocumentMatchIterator {
  if (typeof fieldValue === 'string' && fieldValue.startsWith('*')) {
    stats?.mark('all-documents-iterator')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  if (typeof fieldValue === 'string' && fieldValue.endsWith('*')) {
    let minKey = fieldValue.slice(0, -1)
    let maxKey = fieldValue.slice(0, -1) + '\uffff' // Represents a very large string (for comparison)

    stats?.mark('wildcard-range-iterator', { key: fieldValue, minKey, maxKey })

    return buildIteratorForFieldRange(minKey, maxKey, index, stats)
  }

  const idIterator = index.getDocumentIdsForValue(fieldValue)

  if (idIterator && idIterator.size > 0) {
    const numberOfDocuments = idIterator.size
    // Note: idIterator is a DocumentIdIterator - works for both BTree and Set indexes
    // The index.isBTree flag indicates whether the backing storage supports native O(log n) seeking
    const it = { type: IterateOverIndex, index, key: fieldValue, cost: numberOfDocuments, idIterator, isSortable: index.isBTree }
    stats?.mark('field-value-iterator', { cost: it.cost, iterator: describeIterator(it) })
    return it
  } else {
    stats?.mark('field-value-iterator:none')
    return { type: IterateNoDocuments, index, key: fieldValue, cost: 0, isSortable: true }
  }
}

function buildIteratorForNotFieldMatch(
  condition: FieldMatchNode,
  parameters: QueryParameters,
  indexes: IndexMap,
  stats?: SingleQueryStats
): DocumentMatchIterator {
  // For id field, NOT queries can't be optimized easily, fall back to all documents
  if (condition.field === 'id') {
    stats?.mark('not-query:id-field-all-documents')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  const matchingIndex = indexes[condition.field]

  if (!matchingIndex) {
    // No index available, must iterate all documents
    stats?.mark('not-query:no-index-all-documents')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  if (matchingIndex.isSuperSet) {
    // Index is a superset of all documents, the values are mapped and potentially truncated
    // so we cannot filter based on index values
    stats?.mark('not-query:superset')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  if ('term_min' in condition || 'term_max' in condition) {
    // NOT range queries are complex, fall back to all documents for now
    stats?.mark('not-query:range-all-documents')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  if ('term' in condition) {
    const excludeKey = parameters[condition.term.id]

    if (Array.isArray(excludeKey)) {
      // NOT with multiple values - enumerate all other values
      return buildIteratorForNotMultipleValues(excludeKey, matchingIndex, stats)
    } else {
      // NOT with single value - enumerate all other values
      return buildIteratorForNotSingleValue(excludeKey, matchingIndex, stats)
    }
  }

  throw new Error('NOT query not supported for this field type')
}

function buildIteratorForNotSingleValue(excludeValue: any, index: Index, stats?: SingleQueryStats): DocumentMatchIterator {
  stats?.mark('not-query:single-value-complement')

  // Get all values from the index except the excluded one
  const allValues = Array.from(index.mappedValues())

  stats?.mark('not-query:single-value-complement', { allValuesCount: allValues.length, excludeValue })
  const complementValues = allValues.filter(value => value !== excludeValue)

  if (complementValues.length === 0) {
    // All documents have the excluded value, so NOT query matches nothing
    return { type: IterateNoDocuments, index, key: excludeValue, cost: 0, isSortable: true }
  }

  // Create iterators for all complement values
  const subIterators = complementValues.map(value => buildIteratorForFieldValue(value, index, stats))

  // For NOT queries, this is a union operation - do not apply intersection optimizations
  const optimizedSubIterators = subIterators // Keep all iterators for union
  const optimizedCost = optimizedSubIterators.reduce((sum, it) => sum + it.cost, 0)

  // Use K-merge union if all iterators are sortable
  if (optimizedSubIterators.every(it => it.isSortable)) {
    return {
      type: KMergedUnion,
      cost: optimizedCost,
      iterators: optimizedSubIterators,
      isSortable: true
    }
  } else {
    return {
      type: IterateOverSubIterators,
      operator: 'union',
      cost: optimizedCost,
      iterators: optimizedSubIterators as (
        DocumentMatchIteratorUsingId | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorJoined
      )[],
      isSortable: false
    }
  }
}

function buildIteratorForNotMultipleValues(excludeValues: any[], index: Index, stats?: SingleQueryStats): DocumentMatchIterator {
  stats?.mark('not-query:multiple-values-complement')

  // Get all values from the index except the excluded ones
  const excludeSet = new Set(excludeValues)
  const allValues = Array.from((index as any)._valuesToDocumentIds.keys())
  const complementValues = allValues.filter(value => !excludeSet.has(value))

  if (complementValues.length === 0) {
    // All documents have one of the excluded values, so NOT query matches nothing
    return { type: IterateNoDocuments, index, key: excludeValues[0], cost: 0, isSortable: true }
  }

  // Create iterators for all complement values
  const subIterators = complementValues.map(value => buildIteratorForFieldValue(value, index, stats))

  // For NOT queries, this is a union operation - do not apply intersection optimizations
  const optimizedSubIterators = subIterators // Keep all iterators for union
  const optimizedCost = optimizedSubIterators.reduce((sum, it) => sum + it.cost, 0)

  // Use K-merge union if all iterators are sortable
  if (optimizedSubIterators.every(it => it.isSortable)) {
    return {
      type: KMergedUnion,
      cost: optimizedCost,
      iterators: optimizedSubIterators,
      isSortable: true
    }
  } else {
    return {
      type: IterateOverSubIterators,
      operator: 'union',
      cost: optimizedCost,
      iterators: optimizedSubIterators as (
        DocumentMatchIteratorUsingId | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorJoined
      )[],
      isSortable: false
    }
  }
}

export function buildIteratorForJoinedCondition(
  node: MatchExpressionMultiMatch,
  parameters: QueryParameters,
  indexes: IndexMap,
  stats?: SingleQueryStats,
  options?: OptimizationOptions
): DocumentMatchIterator {
  if (node.operator === 'AND') {
    return buildIntersectionIterator(node.conditions, parameters, indexes, stats, options)
  } else if (node.operator === 'OR') {
    return buildUnionIterator(node.conditions, parameters, indexes, stats)
  } else {
    throw new Error('Unsupported iterator')
  }
}

export function buildIntersectionIterator(
  conditions: Condition[],
  parameters: QueryParameters,
  indexes: IndexMap,
  stats?: SingleQueryStats,
  options?: OptimizationOptions
): DocumentMatchIteratorJoined | DocumentMatchIteratorOverNoDocuments | DocumentMatchIteratorAllDocuments {
  const iteratorsToJoin = conditions.map(c => buildIteratorForGraph(c, parameters, indexes, stats, options, false))

  // Flatten nested intersections and collect all iterators
  const flattenedIterators: (DocumentMatchIteratorUsingId | DocumentMatchIteratorJoined | DocumentMatchIteratorUsingIndex)[] = []
  let minimumCost = Number.MAX_SAFE_INTEGER

  for (const iterator of iteratorsToJoin) {
    if (isIteratorOverNoDocuments(iterator)) {
      stats?.mark('intersection-iterator:none')
      return iterator
    }

    if (isIteratorOverAllDocuments(iterator)) {
      // Skip "all documents" iterators in intersection - they don't constrain the result
      continue
    }

    // Flatten nested intersections
    if (isIteratorUsingJoinsOfIterators(iterator) && iterator.operator === 'intersect') {
      flattenedIterators.push(...iterator.iterators)
      if (iterator.cost < minimumCost) {
        minimumCost = iterator.cost
      }
    } else if (
      isIteratorUsingId(iterator) ||
      isIteratorUsingIndexOfDocuments(iterator) ||
      isIteratorUsingJoinsOfIterators(iterator) ||
      iterator.type === KMergedUnion ||
      iterator.type === KMergedIntersection
    ) {
      flattenedIterators.push(iterator as any)
      if (iterator.cost < minimumCost) {
        minimumCost = iterator.cost
      }
    } else {
      console.warn('Unsupported iterator type for intersection:', iterator)
      stats?.mark('intersection-iterator:unsupported', { iterator: describeIterator(iterator) })
      continue
    }
  }

  // Apply optimization at this level for intersection operations
  let optimizedIterators = flattenedIterators
  if (options?.enableCostBasedShortcut && flattenedIterators.length > 1) {
    optimizedIterators = optimizeSiblingIterators(flattenedIterators, options, stats, 'intersect') as (
      DocumentMatchIteratorUsingId | DocumentMatchIteratorJoined | DocumentMatchIteratorUsingIndex
    )[]

    // Update minimum cost based on optimized iterators
    if (optimizedIterators.length !== flattenedIterators.length) {
      minimumCost = Math.min(...optimizedIterators.map(it => it.cost))
    }
  }

  if (optimizedIterators.length === 0) {
    stats?.mark('intersection-iterator:all-documents')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  if (optimizedIterators.length === 1) {
    const singleIterator = optimizedIterators[0]
    stats?.mark('intersection-iterator:single', { iterator: describeIterator(singleIterator), cost: singleIterator.cost })
    return singleIterator
  }

  // Sort iterators by cost to optimize enumeration (smallest first)
  optimizedIterators.sort((a, b) => a.cost - b.cost)

  // Check if we can use k-merge intersection (all iterators are sortable)
  const allSortable = optimizedIterators.every((it: any) => it.isSortable)
  if (allSortable && optimizedIterators.length > 2) {
    stats?.mark('intersection-iterator:k-merge', {
      cost: minimumCost,
      iteratorCount: optimizedIterators.length,
      iterators: optimizedIterators.map((i: any) => ({
        type: i.type.description,
        cost: i.cost
      }))
    })

    return {
      type: KMergedIntersection,
      cost: minimumCost,
      iterators: optimizedIterators,
      isSortable: true
    }
  }

  // Fall back to traditional nested intersection
  stats?.mark('intersection-iterator:traditional', {
    cost: minimumCost,
    iteratorCount: optimizedIterators.length,
    iterators: optimizedIterators.map((i: any) => ({
      type: i.type.description,
      cost: i.cost,
      description: describeIterator(i)
    }))
  })

  // Use K-merge intersection if all iterators are sortable
  if (allSortable) {
    return {
      type: KMergedIntersection,
      cost: minimumCost,
      iterators: optimizedIterators,
      isSortable: true
    }
  } else {
    return {
      type: IterateOverSubIterators,
      operator: 'intersect',
      cost: minimumCost,
      iterators: optimizedIterators,
      isSortable: false
    }
  }
}

export function buildUnionIterator(
  conditions: Condition[],
  parameters: QueryParameters,
  indexes: IndexMap,
  stats?: SingleQueryStats
): DocumentMatchIteratorJoined | DocumentMatchIteratorAllDocuments {
  const iteratorsToJoin = conditions.map(c => buildIteratorForGraph(c, parameters, indexes))

  // Flatten nested unions and collect all iterators
  const flattenedIterators: (
    DocumentMatchIteratorJoined | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorUsingId
  )[] = []
  let totalCost = 0

  for (const iterator of iteratorsToJoin) {
    if (isIteratorOverAllDocuments(iterator)) {
      stats?.mark('union-iterator:all-documents', { cost: Number.MAX_SAFE_INTEGER })
      return iterator
    }

    if (isIteratorOverNoDocuments(iterator)) {
      // Skip empty iterators in union
      continue
    }

    // Flatten nested unions
    if (isIteratorUsingJoinsOfIterators(iterator) && iterator.operator === 'union') {
      flattenedIterators.push(...iterator.iterators)
      totalCost += iterator.cost
    } else {
      flattenedIterators.push(iterator as any)
      totalCost += (iterator as any).cost
    }
  }

  if (flattenedIterators.length === 0) {
    stats?.mark('union-iterator:none')
    return { type: IterateNoDocuments, cost: 0, isSortable: true }
  }

  if (flattenedIterators.length === 1) {
    const singleIterator = flattenedIterators[0]
    stats?.mark('union-iterator:single', { cost: totalCost, iterator: describeIterator(singleIterator) })
    return singleIterator
  }

  // Check if we can use k-merge union (all iterators are sortable)
  const allSortable = flattenedIterators.every(it => it.isSortable)
  if (allSortable && flattenedIterators.length > 2) {
    stats?.mark('union-iterator:k-merge', {
      cost: totalCost,
      iteratorCount: flattenedIterators.length,
      iterators: flattenedIterators.map(i => ({ type: i.type.description, cost: i.cost }))
    })

    return {
      type: KMergedUnion,
      cost: totalCost,
      iterators: flattenedIterators,
      isSortable: true
    }
  }

  // Fall back to traditional nested union
  stats?.mark('union-iterator:traditional', {
    cost: totalCost,
    iteratorCount: flattenedIterators.length,
    iterators: flattenedIterators.map(i => ({ type: i.type.description, cost: i.cost, description: describeIterator(i) }))
  })

  // Use K-merge union if all iterators are sortable
  if (allSortable) {
    return {
      type: KMergedUnion,
      cost: totalCost,
      iterators: flattenedIterators,
      isSortable: true
    }
  } else {
    return {
      type: IterateOverSubIterators,
      operator: 'union',
      cost: totalCost,
      iterators: flattenedIterators,
      isSortable: false
    }
  }
}
function isPrefixWildcardTerm(condition: FieldMatchNode, parameters: QueryParameters) {
  if (typeof condition !== 'object' || condition === null) {  
    return false
  
  }

  const term = condition.term

  if (!term) {
    return false
  }

  const parameter = parameters[term?.id]

  const values = Array.isArray(parameter) ? parameter : [parameter]

  for (const param of values) {
    if (typeof param !== 'string') {
      continue
    }
    
    if (param.startsWith('*')) {
      return true
    }
  }
  
  return false
}
